import { css } from "@emotion/css";
import { ClickAwayListener, Fade, Popper } from "@mui/material";
import { borderRadius, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { PropsWithChildren } from "react";
import { useTransitionComponentOverride } from "../../transition";
import type { SupportedAriaPopupRole } from "../../utils/SupportedAriaPopupRole";
export interface PopoverProps {
    placement: PopoverPlacement;
    onClose?: () => void;
    anchorEl: null | HTMLElement;
    open: boolean;
    elementId?: string;
    accessibleName?: string;
    accessibleRole?: SupportedAriaPopupRole;
    boundaryPadding?: number;
}
export const popoverPlacementOptions = ["bottom-end", "bottom-start", "top-start", "top-end", "left-start", "left-end", "right-start", "right-end"] as const;
export type PopoverPlacement = (typeof popoverPlacementOptions)[number];
export function Popover({ children, open, anchorEl, placement = "left-start", onClose, elementId, accessibleName, accessibleRole = "dialog", boundaryPadding }: PropsWithChildren<PopoverProps>) {
    const TransitionComponent = useTransitionComponentOverride() ?? Fade;
    const onCloseClicked = () => {
        onClose?.();
    };
    if (!anchorEl) {
        return null;
    }
    return (<Popper id={elementId} role={accessibleRole} aria-label={accessibleName} className={popoverContainerStyles} open={open} anchorEl={anchorEl} placement={placement} modifiers={[
            { name: "flip", enabled: false },
            { name: "offset", enabled: true, options: { offset: [0, 8] } },
            { name: "preventOverflow", enabled: true, options: { padding: boundaryPadding, boundariesElement: "window" } },
        ]} transition>
            {({ TransitionProps }) => (<ClickAwayListener onClickAway={onCloseClicked}>
                    <TransitionComponent {...TransitionProps} timeout={{ enter: 350, exit: 100 }}>
                        <div>{children}</div>
                    </TransitionComponent>
                </ClickAwayListener>)}
        </Popper>);
}
const popoverContainerStyles = css({
    zIndex: "auto",
    isolation: "isolate",
    borderRadius: borderRadius.small,
    border: `1px solid ${themeTokens.color.border.primary}`,
    backgroundColor: themeTokens.color.background.primary.default,
    boxShadow: themeTokens.shadow.medium,
    overflow: "clip",
    minHeight: "77px",
    font: text.body.regular.medium,
    color: themeTokens.color.text.primary,
});
